import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, FileText, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface AuditEntry {
  id: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  resource_name: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface UserOption {
  id: string;
  label: string;
}

const PAGE_SIZE = 100;

const ACTION_COLORS: Record<string, string> = {
  accessed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  created: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  edited: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  deleted: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  restored: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  permanent_deleted: "bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200",
};

const RESOURCE_LABELS: Record<string, string> = {
  report: "Report",
  query: "Query / Report",
  dashboard: "Dashboard",
  chat: "Chat",
  workflow: "Workflow",
  ai_agent: "AI Agent",
};

const AuditLog = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [filterUser, setFilterUser] = useState<string>("all");
  const [filterResource, setFilterResource] = useState<string>("all");
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterDateFrom, setFilterDateFrom] = useState<Date | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<Date | undefined>(undefined);

  // Distinct users for the filter dropdown
  const [users, setUsers] = useState<UserOption[]>([]);

  // Fetch distinct users who have audit entries
  useEffect(() => {
    if (!profile?.client_id) return;

    const fetchUsers = async () => {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("user_id, user_email, user_name")
        .eq("client_id", profile.client_id);

      if (error) return;

      const seen = new Map<string, UserOption>();
      (data ?? []).forEach((row) => {
        if (!seen.has(row.user_id)) {
          seen.set(row.user_id, {
            id: row.user_id,
            label: row.user_name || row.user_email || row.user_id.slice(0, 8),
          });
        }
      });
      setUsers(Array.from(seen.values()));
    };

    fetchUsers();
  }, [profile?.client_id]);

  const buildQuery = (offset: number) => {
    let query = supabase
      .from("audit_logs")
      .select("id, user_id, user_email, user_name, action, resource_type, resource_id, resource_name, metadata, created_at")
      .eq("client_id", profile!.client_id)
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE_SIZE);

    if (filterUser !== "all") {
      query = query.eq("user_id", filterUser);
    }
    if (filterResource !== "all") {
      query = query.eq("resource_type", filterResource);
    }
    if (filterAction !== "all") {
      query = query.eq("action", filterAction);
    }
    if (filterDateFrom) {
      const fromISO = new Date(filterDateFrom);
      fromISO.setHours(0, 0, 0, 0);
      query = query.gte("created_at", fromISO.toISOString());
    }
    if (filterDateTo) {
      const toISO = new Date(filterDateTo);
      toISO.setHours(23, 59, 59, 999);
      query = query.lte("created_at", toISO.toISOString());
    }

    return query;
  };

  // Fetch initial audit log entries (reset when filters change)
  useEffect(() => {
    if (!profile?.client_id) {
      setLoading(false);
      return;
    }

    const fetchEntries = async () => {
      setLoading(true);

      const { data, error } = await buildQuery(0);

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        setEntries([]);
        setHasMore(false);
      } else {
        const rows = (data ?? []) as AuditEntry[];
        setHasMore(rows.length > PAGE_SIZE);
        setEntries(rows.slice(0, PAGE_SIZE));
      }
      setLoading(false);
    };

    fetchEntries();
  }, [profile?.client_id, filterUser, filterResource, filterAction, filterDateFrom, filterDateTo]);

  const handleLoadMore = async () => {
    if (!profile?.client_id) return;
    setLoadingMore(true);

    const { data, error } = await buildQuery(entries.length);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      const rows = (data ?? []) as AuditEntry[];
      setHasMore(rows.length > PAGE_SIZE);
      setEntries((prev) => [...prev, ...rows.slice(0, PAGE_SIZE)]);
    }
    setLoadingMore(false);
  };

  const handleFilterChange = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value);
  };

  if (!profile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <FileText className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      <div>
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          Track all user activity across reports, dashboards, chat, and AI agents.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">User:</span>
          <Select value={filterUser} onValueChange={(v) => handleFilterChange(setFilterUser, v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Resource:</span>
          <Select value={filterResource} onValueChange={(v) => handleFilterChange(setFilterResource, v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="report">Report</SelectItem>
              <SelectItem value="dashboard">Dashboard</SelectItem>
              <SelectItem value="chat">Chat</SelectItem>
              <SelectItem value="workflow">Workflow</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Action:</span>
          <Select value={filterAction} onValueChange={(v) => handleFilterChange(setFilterAction, v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="accessed">Accessed</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="edited">Edited</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="restored">Restored</SelectItem>
              <SelectItem value="permanent_deleted">Permanently Deleted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">From:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[150px] justify-start text-left font-normal",
                  !filterDateFrom && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filterDateFrom ? format(filterDateFrom, "MMM d, yyyy") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterDateFrom}
                onSelect={setFilterDateFrom}
                disabled={(date) =>
                  date > new Date() || (filterDateTo ? date > filterDateTo : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">To:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[150px] justify-start text-left font-normal",
                  !filterDateTo && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filterDateTo ? format(filterDateTo, "MMM d, yyyy") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={filterDateTo}
                onSelect={setFilterDateTo}
                disabled={(date) =>
                  date > new Date() || (filterDateFrom ? date < filterDateFrom : false)
                }
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {(filterDateFrom || filterDateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterDateFrom(undefined);
              setFilterDateTo(undefined);
            }}
          >
            Clear dates
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : entries.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-1">No audit entries</p>
          <p className="text-sm text-muted-foreground">Activity will appear here as users interact with the platform.</p>
        </Card>
      ) : (
        <>
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource Type</TableHead>
                  <TableHead>Resource</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {format(new Date(entry.created_at), "MMM d, yyyy HH:mm")}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {entry.user_name || "Unknown"}
                        </span>
                        {entry.user_email && (
                          <span className="text-xs text-muted-foreground">{entry.user_email}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={ACTION_COLORS[entry.action] || ""}
                      >
                        {entry.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {RESOURCE_LABELS[entry.resource_type] || entry.resource_type}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {entry.resource_name || entry.resource_id || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </span>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Load more
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AuditLog;
